/* Base Layout
Author: Maxwell Levene @maxlevene920- Ellmarr
Date: 04/30/2025
Description: Dialog component for updating bike maintenance status and details.
Provides form interface for maintenance staff to log repairs and updates.
*/

import React, { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger,
  DialogFooter,
  DialogClose
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Database } from "@/integrations/supabase/types";
import { Input } from "@/components/ui/input";

// Define types based on Supabase database enums
type MaintenanceStatus = Database["public"]["Enums"]["maintenance_status"];
type BikeStatus = Database["public"]["Enums"]["bike_status"];
type IssueType = Database["public"]["Enums"]["issue_type"];

interface UpdateMaintenanceDialogProps {
  issue: {
    id: string;
    bike_id: string;
    status: MaintenanceStatus;
    issue_type: IssueType;
    notes: string | null;
    cost?: number | null;
    bikes?: {
      serial_number: string;
      status: BikeStatus;
    };
  };
  onUpdate: () => void;
}

export function UpdateMaintenanceDialog({ issue, onUpdate }: UpdateMaintenanceDialogProps) {
  const [status, setStatus] = useState<MaintenanceStatus>(issue.status);
  const [issueType, setIssueType] = useState<IssueType>(issue.issue_type || 'other');
  const [notes, setNotes] = useState(issue.notes || "");
  const [cost, setCost] = useState<string>(issue.cost?.toString() || "");
  const [bikeStatus, setBikeStatus] = useState<BikeStatus>(issue.bikes?.status || "inactive");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  
  const handleSubmit = async () => {
    setIsSubmitting(true);
    
    try {
      let numericCost: number | null = null;
      if (cost.trim() !== "") {
        numericCost = Number(cost);
        if (isNaN(numericCost) || numericCost < 0) {
          toast.error('Please provide a valid maintenance fee (0 or greater)');
          setIsSubmitting(false);
          return;
        }
      }

      // First update the maintenance issue
      const { error: updateError } = await supabase
        .from("maintenance_issues")
        .update({
          status,
          issue_type: issueType,
          notes,
          cost: numericCost
        })
        .eq("id", issue.id);

      if (updateError) throw updateError;
      
      // Update the bike status if needed
      const { error: bikeError } = await supabase
        .from("bikes")
        .update({
          status: bikeStatus
        })
        .eq("id", issue.bike_id);
        
      if (bikeError) throw bikeError;
      
      toast.success("Maintenance issue updated successfully");
      // Call onUpdate silently without additional toast notification
      onUpdate();
      setOpen(false);
    } catch (error: any) {
      toast.error(`Failed to update maintenance issue: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline">Update Issue</Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Update Maintenance Issue</DialogTitle>
        </DialogHeader>
        <div className="grid gap-4 py-4">
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="bike" className="text-right">
              Bike
            </Label>
            <div className="col-span-3">
              <p className="text-sm font-medium">{issue.bikes?.serial_number}</p>
            </div>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="issueType" className="text-right">
              Issue Type
            </Label>
            <Select
              value={issueType}
              onValueChange={(value: IssueType) => setIssueType(value)}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select issue type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="tires">Tires</SelectItem>
                <SelectItem value="brakes">Brakes</SelectItem>
                <SelectItem value="headset">Headset</SelectItem>
                <SelectItem value="wheels">Wheels</SelectItem>
                <SelectItem value="battery">Battery</SelectItem>
                <SelectItem value="missing">Missing</SelectItem>
                <SelectItem value="unreported">Unreported</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="status" className="text-right">
              Issue Status
            </Label>
            <Select
              value={status}
              onValueChange={(value: MaintenanceStatus) => setStatus(value)}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="reported">Reported</SelectItem>
                <SelectItem value="in_progress">In Progress</SelectItem>
                <SelectItem value="resolved">Resolved</SelectItem>
                <SelectItem value="broken">Broken</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="bikeStatus" className="text-right">
              Bike Status
            </Label>
            <Select
              value={bikeStatus}
              onValueChange={(value: BikeStatus) => setBikeStatus(value)}
            >
              <SelectTrigger className="col-span-3">
                <SelectValue placeholder="Select bike status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="cost" className="text-right">
              Cost (£)
            </Label>
            <Input
              id="cost"
              type="number"
              min="0"
              step="0.01"
              value={cost}
              onChange={(e) => setCost(e.target.value.replace(/[^0-9.]/g, ""))}
              placeholder="Enter maintenance cost"
              className="col-span-3"
            />
          </div>
          
          <div className="grid grid-cols-4 items-center gap-4">
            <Label htmlFor="notes" className="text-right">
              Notes
            </Label>
            <Textarea
              id="notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="col-span-3"
              rows={4}
            />
          </div>
        </div>
        <DialogFooter>
          <DialogClose asChild>
            <Button variant="outline">Cancel</Button>
          </DialogClose>
          <Button 
            type="submit" 
            onClick={handleSubmit} 
            disabled={isSubmitting}
          >
            {isSubmitting ? "Updating..." : "Update Issue"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
